import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { LoggerService } from './logger.service';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class EntityService {

  private api_service_base = 'api_service_';

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
    private authService: AuthService
  ) { }

  getApiURL(entityName: string): string {
    const apiUrl = environment[this.api_service_base + entityName as keyof typeof environment];
    if (!apiUrl) {
      this.logger.error("EntityService.getApiURL: ", this.api_service_base + entityName);
      throw new Error(`API URL for ${entityName} not found in environment`);
    }
    return apiUrl.toString();
  }

  getAll(entityName: string): Observable<any[]> {
    return this.http.get<any[]>(this.getApiURL(entityName));
  }

  getById(entityName: string, id: number): Observable<any> {
    return this.http.get<any[]>(this.getApiURL(entityName)+'/'+`${id}`);
  }

  create(entityName: string, data: any): Observable<any> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser || !currentUser.id) {
      return throwError(() => new Error('Sesión no válida. Por favor, vuelve a iniciar sesión.'));
    }
    const body = { data: data, userid: currentUser.id };
    return this.http.post<any[]>(this.getApiURL(entityName), body);
  }

  update(entityName: string, id: number, data: any): Observable<any> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser || !currentUser.id) {
      return throwError(() => new Error('Sesión no válida. Por favor, vuelve a iniciar sesión.'));
    }
    const body = { data: data, userid: currentUser.id };
    return this.http.put<any[]>(this.getApiURL(entityName)+'/'+`${id}`, body);
  }

  delete(entityName: string, id: number): Observable<any> {
    return this.http.delete<any[]>(this.getApiURL(entityName)+'/'+`${id}`);
  }

  filter(entityName: string, filters: any): Observable<any[]> {
    let queryString = Object.keys(filters)
      .map(key => `${key}=${filters[key]}`)
      .join('&');
    return this.http.get<any[]>(this.getApiURL(entityName)+'?'+`${queryString}`);
  }

  getEntityFields(entityName: string): any[] {
    const entityFieldDefinitions: { [key: string]: any[] } = {
      Tenant: [
        { name: 'name', label: 'Nombre del Tenant', type: 'text', entity:'' },
        { name: 'description', label: 'Descripción', type: 'text', entity:'' }
      ],
      Sociedad: [
        { name: 'name', label: 'Nombre', type: 'text', entity:'' },
        { name: 'country', label: 'Pais', type: 'text', entity:'' },
        { name: 'city', label: 'Ciudad', type: 'text', entity:'' },
        { name: 'address', label: 'Dirección', type: 'text', entity:'' },
        { name: 'phonenumber', label: 'Teléfono', type: 'text', entity:'' },
        { name: 'client', label: 'Clientes', type: 'select', entity:'client' },
        { name: 'departments', label: 'Departamentos', type: 'multi-choice', entity:'department' },
        { name: 'contacts', label: 'Contactos', type: 'multi-choice', entity:'contact' }
      ],
      Departamento: [
        { name: 'name', label: 'Nombre', type: 'text', entity:'' },
        { name: 'description', label: 'Descripción', type: 'text', entity:'' },
        { name: 'headdepartmentid', label: 'Jefe del Departamento', type: 'text', entity:'' },
        { name: 'location', label: 'Localización', type: 'text', entity:'' },
        { name: 'society', label: 'Sociedades', type: 'select', entity:'society' }
      ],
      Cliente: [
        { name: 'name', label: 'Nombre', type: 'text', entity:'' },
        { name: 'address', label: 'Dirección', type: 'text', entity:'' },
        { name: 'email', label: 'Correo Electrónico', type: 'text', entity:'' },
        { name: 'phonenumber', label: 'Teléfono', type: 'text', entity:'' },
        { name: 'societies', label: 'Sociedades', type: 'multi-choice', entity:'society' },
        { name: 'systems', label: 'Sistemas', type: 'multi-choice', entity:'system' }
      ],
      Contacto: [
        { name: 'name', label: 'Nombre', type: 'text', entity:'' },
        { name: 'address', label: 'Dirección', type: 'text', entity:'' },
        { name: 'email', label: 'Correo Electrónico', type: 'text', entity:'' },
        { name: 'phonenumber', label: 'Teléfono', type: 'text', entity:'' }
      ],
      Perfil: [
        { name: 'name', label: 'Nombre', type: 'text', entity:'' },
        { name: 'description', label: 'Descripción', type: 'text', entity:'' }
      ],
      Subsistema: [
        { name: 'name', label: 'Nombre', type: 'text', entity:'' },
        { name: 'system', label: 'Sistemas', type: 'select', entity:'system' }
      ],
      Sistema: [
        {name: 'name', label: 'Nombre', type: 'text-with-similarity',entity: '', similarityCheck: {entityKey: 'system', excludeIdField: 'id', debounceMs: 400}},
        {name: 'nameSlug', label: 'Nombre Normalizado', type: 'readonly-derived', entity: '', derivedFrom: 'name', transform: 'slugify' },
        { name: 'description', label: 'Descripción', type: 'text', entity:'' },
        { name: 'frameworkversion', label: 'Versión', type: 'text', entity:'' },
        { name: 'client', label: 'Cliente', type: 'select', entity:'client' },
        // 1c: multi-choice INTERACTIVO de tecnologías.
        // La bandera 'interactive: true' indica al motor del form que debe
        // renderizar <app-multi-select> en vez del visualizador read-only.
        // El resto de multi-choice del proyecto (Sociedad.departments,
        // Cliente.systems, etc.) NO llevan la bandera y mantienen su
        // comportamiento original.
        {name: 'technologies', label: 'Tecnologías', type: 'multi-choice', entity: 'technology', interactive: true }
      ],
      Tecnología: [ 
        { name: 'name', label: 'Nombre', type: 'text-with-similarity', entity: '', similarityCheck: { entityKey: 'technology', excludeIdField: 'id', debounceMs: 400 } },
        { name: 'nameSlug', label: 'Nombre Normalizado', type: 'readonly-derived', entity: '', derivedFrom: 'name', transform: 'slugify' },
        { name: 'frameworkversion', label: 'Versión', type: 'text', entity:'' }
      ],
      Tipo: [
        { name: 'name', label: 'Nombre', type: 'text', entity:'' },
        { name: 'description', label: 'Descripción', type: 'text', entity:'' },
        { name: 'category', label: 'Categoría', type: 'text', entity:'' }
      ]
    };

    return entityFieldDefinitions[entityName.toString()] || [];
  }
}